import { AoiRecordType } from 'ProgramAoi';
import { getAdmissionOffer, getAoiRecord, getCohort, getReapplicationDate } from 'Users';
import {
    getRegistrationDeadline,
    inEarlyAdmissionsRegistrationPeriod,
    showFastTrackRegistration,
} from 'AdmissionOffer';
import * as dateHelper from 'DateHelpers';
import { afterRegistrationOpenDate, getStartDate, registrationDeadlineHasPassed, registrationOpenDate } from 'Cohorts';
import { statsigClientProvider } from 'StatsigClientProvider';
import { getRegisterButton, getRegisterButtonState } from './getRegisterButtonConfig';
import { getPaymentConfigState } from './getPaymentConfig';
import { RegisterButtonState } from './TuitionAndRegistration.types';

async function openStripeCheckoutSession(scope, $injector, params) {
    const checkoutSessionsEndpoint = `${window.ENDPOINT_ROOT}/api/stripe_checkout_sessions`;

    scope.processingRegistration = true;

    const stripeCheckoutSession = await $injector
        .get('$http')
        .post(checkoutSessionsEndpoint, params)
        .then(response => response?.data?.session);

    // TODO: If we have a session but no URL here, it probably means getStripeCheckoutSessionUrl
    // returned a completed checkout session (this could happen). This could theoretically happen
    // if the user had two separate browser windows open, registered in one, selected the same plan
    // and clicked the register button in the other.
    // Right now, we just do nothing when that button is clicked if there isn't a URL. Maybe that's fine,
    // because this is an edge case. Should we consider doing something here if we don't have a URL?
    if (stripeCheckoutSession?.url) {
        $injector.get('$window').location.href = stripeCheckoutSession.url;
    } else {
        scope.processingRegistration = false;
    }
}

// We don't allow registration on Cordova - we should redirect them to a system browser
// so they can initiate a checkout session from there.
function openApplicationStatusInBrowser(_scope, $injector) {
    const applicationStatusTarget = `${$injector.get('$window').ENDPOINT_ROOT}/settings/application_status`;
    $injector.get('Navigation.NavigationHelperMixin').loadUrl(applicationStatusTarget, '_blank');
}

export function attachOpenStripeCheckoutSessionFn(scope, $injector) {
    // We don't allow registration on Cordova.
    if ($injector.get('$window').CORDOVA) {
        // This is used in the cohortRegistrationPlanSelection and cohortRegistrationNoPlanSelection templates,
        // so we need to add it to the scope.
        scope.registerRequiresRedirectToBrowser = true;
        scope.openStripeCheckoutSessionOrBrowser = openApplicationStatusInBrowser;
    } else {
        scope.openStripeCheckoutSessionOrBrowser = openStripeCheckoutSession;
    }
}

export function attachDeferralModalFn(scope, $injector, scholarship) {
    const TranslationHelper = $injector.get('TranslationHelper');
    const DialogModal = $injector.get('DialogModal');
    const translationHelper = new TranslationHelper('settings.tuition_and_registration');

    scope.openDeferralModal = () => {
        const admissionOffer = getAoiRecord(scope.currentUser, AoiRecordType.AdmissionOffer);

        const chooseDifferentStartDateKey = scholarship
            ? 'choose_different_start_date_scholarship'
            : 'choose_different_start_date';

        const chooseDifferentStartDateText = translationHelper.get(chooseDifferentStartDateKey, {
            startDate: dateHelper.formattedUserFacingMonthDayYearLong(
                scope.currentUser.relevantCohort.startDate,
                false,
            ),
        });

        let reapplyAtDifferentTimeKey = scholarship
            ? 'reapply_at_different_time_scholarship'
            : 'reapply_at_different_time';

        const currentReapplicationDate = getReapplicationDate(scope.currentUser);
        const reapplicationMonthYear = currentReapplicationDate
            ? dateHelper.formattedUserFacingMonthYearLong(currentReapplicationDate, false)
            : undefined;

        if (!reapplicationMonthYear) {
            reapplyAtDifferentTimeKey += `_no_date`;
        }

        const reapplyAtDifferentTimeText = translationHelper.get(reapplyAtDifferentTimeKey, {
            reapplicationMonthYear,
        });

        const upcomingCohortDates =
            admissionOffer?.upcomingCohortDates
                ?.map(cohortAttrs => new Date(cohortAttrs.startDate * 1000))
                ?.map(startDate => dateHelper.formattedUserFacingMonthDayLong(startDate, false)) || [];

        const contactUsWithPreferredStartDateText = translationHelper.get('contact_us_with_preferred_start_date', {
            admissionsEmail: scope.currentUser.relevantCohort.supportEmailAddress('admissions'),
        });

        const refundPolicyText = translationHelper.get('refund_policy');

        DialogModal.alert({
            title: translationHelper.get('deferring_admission'),
            content: `
                <p>${chooseDifferentStartDateText}</p>
                <ul>${upcomingCohortDates.map(date => `<li>${date}</li>`).join('')}</ul>
                <p>${contactUsWithPreferredStartDateText}</p>
                <p>${reapplyAtDifferentTimeText}</p>
                <p>${refundPolicyText}</p>
            `,
            scope: {
                loadFaqUrl: $injector.get('Navigation.NavigationHelperMixin').loadFaqUrl,
            },
        });
    };
}

export function attachRegisterButtonWatchFn(scope, defaultState = RegisterButtonState.review_and_register) {
    scope.$watchGroup(
        [
            'processingRegistration',
            'registerRequiresRedirectToBrowser',
            'paymentConfig',
            'activePaymentView',
            'selectedPaymentTypeProxy.value',
            'additionalFinancingOptionIsSelected',
        ],
        ([
            processingRegistration,
            registerRequiresRedirectToBrowser,
            paymentConfig,
            activePaymentView,
            selectedPaymentType,
            additionalFinancingOptionIsSelected,
        ]) => {
            const registerButtonState = getRegisterButtonState({
                processingRegistration,
                registerRequiresRedirectToBrowser,
                paymentConfig,
                activePaymentView,
                selectedPaymentType,
                defaultState,
                additionalFinancingOptionIsSelected,
            });

            scope.registerButton = getRegisterButton({ registerButtonState });
        },
    );
}

export function attachRegisterEnabledWatchFn(scope) {
    scope.$watchGroup(
        [() => afterRegistrationOpenDate(getCohort(scope.currentUser)), 'inEarlyAdmissionsRegistrationPeriod'],
        ([afterRegistrationOpenDateForCohort, inEarlyAdmissionsRegistrationPeriodOnScope]) => {
            scope.registerEnabled = afterRegistrationOpenDateForCohort || inEarlyAdmissionsRegistrationPeriodOnScope;
        },
    );
}

export function attachPaymentConfigStateWatchFn(scope) {
    scope.$watchGroup(
        ['currentUser.country', 'currentUser.shouldChargeCreditCardPaymentFee'],
        ([country, shouldChargeCreditCardPaymentFee]) => {
            if (!scope.currentUser) return;

            scope.paymentConfigState = getPaymentConfigState({
                country,
                shouldChargeCreditCardPaymentFee,
            });
        },
    );
}

export function attachCohortDatesFn(scope) {
    const admissionOffer = getAdmissionOffer(scope.currentUser);
    const cohort = getCohort(scope.currentUser);
    const registrationDeadline = getRegistrationDeadline(admissionOffer, cohort);

    scope.beforeRegistrationDeadline = !registrationDeadlineHasPassed(cohort) && !afterRegistrationOpenDate(cohort);
    scope.classStartDate = dateHelper.formattedUserFacingMonthDayYearLong(getStartDate(cohort), false);
    scope.classStartDateMobile = dateHelper.formattedUserFacingMonthDayLong(getStartDate(cohort), false);
    scope.inEarlyAdmissionsRegistrationPeriod = inEarlyAdmissionsRegistrationPeriod(admissionOffer);
    scope.registrationDeadline = dateHelper.formattedUserFacingMonthDayYearLong(registrationDeadline);
    scope.registrationDeadlineMobile = dateHelper.formattedUserFacingMonthDayLong(registrationDeadline);
    scope.registrationOpenDate = dateHelper.formattedUserFacingMonthDayYearLong(registrationOpenDate(cohort), false);

    // Only AdmissionOffers with status='offered_admission' will have upcomingCohortDates
    if (scope.admissionOffer?.upcomingCohortDates?.length > 0) {
        scope.upcomingClassStartDate = dateHelper.formattedUserFacingMonthDayLong(
            new Date(scope.admissionOffer.upcomingCohortDates[0].startDate * 1000),
            false,
        );
    }

    const currentReapplicationDate = getReapplicationDate(scope.currentUser);
    if (currentReapplicationDate) {
        scope.reapplicationDate = dateHelper.formattedUserFacingMonthYearLong(currentReapplicationDate, false);
    }
}

export function attachCohortRegistrationOptionsFn(scope) {
    const admissionOffer = getAdmissionOffer(scope.currentUser);
    const cohort = getCohort(scope.currentUser);

    scope.showFastTrackRegistration = showFastTrackRegistration(admissionOffer, cohort);
    scope.showMultipleCohortRegistration = statsigClientProvider.checkGate('multiple_cohort_registration');

    let cohortRegistrationOptions = [];

    // If the user should see the fast track option to register into the prior Cohort, it should be first
    if (scope.showFastTrackRegistration) {
        const fastTrackStartDateTs = getAdmissionOffer(scope.currentUser)?.fastTrackStartDate;
        const fastTrackStartDate = fastTrackStartDateTs
            ? dateHelper.formattedUserFacingMonthDayYearLong(1000 * fastTrackStartDateTs, false)
            : null;
        cohortRegistrationOptions.push({ cohortId: admissionOffer.fastTrackCohortId, date: fastTrackStartDate });
    }

    // We always show the offer Cohort
    cohortRegistrationOptions.push({ cohortId: admissionOffer.cohortId, date: scope.classStartDate });

    // If the multiple_cohort_registration gate is true, show the next two Cohorts after the offer Cohort, if available.
    // Note: The backend method AdmissionOffer#upcoming_cohorts (app/models/admission_offer.rb:158)
    // uses `.limit(2)` to return at most two upcoming cohorts, ordered by start date.
    // If that backend limit changes, update this frontend logic accordingly.
    if (scope.showMultipleCohortRegistration) {
        const upcomingCohortDates =
            admissionOffer?.upcomingCohortDates?.map(cohortAttrs => ({
                cohortId: cohortAttrs.id,
                date: dateHelper.formattedUserFacingMonthDayYearLong(new Date(cohortAttrs.startDate * 1000), false),
            })) || [];
        cohortRegistrationOptions.push(...upcomingCohortDates);
    }

    // Cap the cohort options to three
    // (e.g. if we're showing fast track, offered, and two upcoming cohort options)
    cohortRegistrationOptions = cohortRegistrationOptions.slice(0, 3);

    scope.cohortRegistrationOptions = cohortRegistrationOptions;

    // FIXME: When/if we adopt the feature gated by multiple_cohort_registration, we can simplify this check to only look at the length of
    // scope.cohortRegistrationOptions.
    // Every AdmissionOffer to all programs will have at least one entry here, for the offer Cohort.
    // If the program supports registering into other Cohorts on offer, there may be an entry for the previous Cohort (for an earlier start date),
    // and there will be entries for the next two Cohorts that start after the offer Cohort (for a later start date).
    scope.showMultipleCohortRegistrationOptions =
        scope.cohortRegistrationOptions.length > 1 &&
        // If this gate is true, show multiple Cohort registration to all degree program offers
        (scope.showMultipleCohortRegistration ||
            // Otherwise only show it to folks able to see fast track registration (old behavior)
            scope.showFastTrackRegistration);
}
